import React, { useState, useEffect, useRef } from "react";
import "./AddProductModal.css";
import { XMarkIcon, ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { getAllPrepStations } from "../../../../../../config/Services";

const AddProductModal = ({ open, onClose, onAdd, loading }) => {
  const [itemName, setItemName] = useState("");
  const [sellingPrice, setSellingPrice] = useState("");
  const [prepStations, setPrepStations] = useState([]);
  const [prepStationId, setPrepStationId] = useState("");
  const [prepDropdownOpen, setPrepDropdownOpen] = useState(false);
  const [prepSearch, setPrepSearch] = useState("");
  const [menuStyle, setMenuStyle] = useState({});

  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  const locId = localStorage.getItem("locId");
  const orgId = localStorage.getItem("orgId");

  useEffect(() => {
    if (!open || !locId || !orgId) return;
    getAllPrepStations({
      page: 1,
      pageSize: 100,
      sortByUpdatedAt: "DESC",
      storeLocationId: [locId],
    })
      .then((res) => setPrepStations(res?.data?.data?.data?.data || []))
      .catch(() => setPrepStations([]));
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!prepDropdownOpen) return;
    const handleOutside = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setPrepDropdownOpen(false);
        setPrepSearch("");
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [prepDropdownOpen]);

  // Close on Escape
  useEffect(() => {
    if (!prepDropdownOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") {
        setPrepDropdownOpen(false);
        setPrepSearch("");
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [prepDropdownOpen]);

  // Auto-focus search when open
  useEffect(() => {
    if (prepDropdownOpen) {
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [prepDropdownOpen]);

  const openDropdown = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuStyle({
        position: "fixed",
        bottom: window.innerHeight - rect.top + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    }
    setPrepDropdownOpen(true);
  };

  const filteredStations = prepStations.filter((s) =>
    s.displayName?.toLowerCase().includes(prepSearch.toLowerCase())
  );

  const selectedStation = prepStations.find((s) => s.id === prepStationId);

  const handlePriceChange = (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, "");
    const parts = raw.split(".");
    const cleaned = parts.length > 2 ? parts[0] + "." + parts.slice(1).join("") : raw;
    if (raw.length > 1 && raw[0] === "0" && raw[1] !== ".") return;
    setSellingPrice(cleaned);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const price = parseFloat(sellingPrice);
    if (!itemName.trim() || !price || price <= 0) return;
    onAdd({
      itemName: itemName.trim(),
      sellingPrice: price,
      prepStationId: prepStationId || undefined,
      prepStationName: selectedStation?.displayName || undefined,
    });
  };

  const resetForm = () => {
    setItemName("");
    setSellingPrice("");
    setPrepStationId("");
    setPrepDropdownOpen(false);
    setPrepSearch("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Clear the form whenever the modal closes, so it always reopens blank.
  // Covers every close path — overlay click, cross, Cancel, and the parent
  // closing it after a successful "Add to Cart".
  useEffect(() => {
    if (!open) resetForm();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectStation = (id) => {
    setPrepStationId(id);
    setPrepDropdownOpen(false);
    setPrepSearch("");
  };

  const canSubmit = itemName.trim().length > 0;

  if (!open) return null;

  return (
    <div className="apm-overlay" onClick={handleClose}>
      <div className="apm-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="apm-header">
          <div>
            <p className="apm-tag">CART ACTION</p>
            <h2 className="apm-title">Add Product</h2>
          </div>
          <button className="apm-close" onClick={handleClose}>
            <XMarkIcon style={{ width: 20, height: 20 }} />
          </button>
        </div>

        {/* Form */}
        <form className="apm-form" onSubmit={handleSubmit}>
          {/* Item Name */}
          <div className="apm-field">
            <label className="apm-label">Item Name</label>
            <input
              className="apm-input"
              type="text"
              placeholder="Enter item name"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              autoFocus
            />
          </div>

          {/* Selling Price */}
          <div className="apm-field">
            <label className="apm-label">Selling Price (₹)</label>
            <div className="apm-price-row">
              <span className="apm-price-prefix">₹</span>
              <input
                className="apm-input apm-price-input"
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                value={sellingPrice}
                onChange={handlePriceChange}
              />
            </div>
          </div>

          {/* Prep Station — searchable dropdown */}
          {prepStations.length > 0 && (
            <div className="apm-field">
              <label className="apm-label">Prep Station</label>
              <div className="apm-dropdown">
                <button
                  type="button"
                  ref={triggerRef}
                  className={`apm-dropdown-trigger${prepDropdownOpen ? " open" : ""}`}
                  onClick={openDropdown}
                >
                  <span className={selectedStation ? "" : "apm-dropdown-placeholder"}>
                    {selectedStation?.displayName || "Select station (optional)"}
                  </span>
                  <ChevronDownIcon
                    className={`apm-dropdown-chevron${prepDropdownOpen ? " rotated" : ""}`}
                    style={{ width: 16, height: 16 }}
                  />
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="apm-actions">
            <button type="button" className="apm-btn-cancel" onClick={handleClose}>
              Cancel
            </button>
            <button
              type="submit"
              className={`apm-btn-add ${!canSubmit || loading ? "disabled" : ""}`}
              disabled={!canSubmit || loading}
            >
              {loading ? "Adding..." : "Add to Cart"}
            </button>
          </div>
        </form>
      </div>

      {/* Dropdown menu rendered outside the panel via fixed positioning */}
      {prepDropdownOpen && (
        <div
          ref={menuRef}
          className="apm-dropdown-menu"
          style={menuStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="apm-dropdown-search">
            <MagnifyingGlassIcon style={{ width: 14, height: 14, color: "#9ca3af", flexShrink: 0 }} />
            <input
              ref={searchRef}
              type="text"
              className="apm-dropdown-search-input"
              placeholder="Search stations..."
              value={prepSearch}
              onChange={(e) => setPrepSearch(e.target.value)}
            />
          </div>
          <div className="apm-dropdown-list">
            {filteredStations.map((station) => (
              <button
                type="button"
                key={station.id}
                className={`apm-dropdown-item${prepStationId === station.id ? " selected" : ""}`}
                onClick={() => handleSelectStation(station.id)}
              >
                {station.displayName}
              </button>
            ))}
            {filteredStations.length === 0 && (
              <div className="apm-dropdown-empty">No stations found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddProductModal;
